import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CompanyAdminAuthProvider } from './contexts/CompanyAdminAuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { PartnerAuthProvider } from './contexts/PartnerAuthContext';

// Old Admin Panel Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';

// SuperAdmin Pages
import SuperAdminLoginPage from './pages/superadmin/LoginPage';
import SuperAdminDashboardPage from './pages/superadmin/DashboardPage';
import SuperAdminCompaniesPage from './pages/superadmin/CompaniesPage';
import SuperAdminCompanyUsersPage from './pages/superadmin/CompanyUsersPage';
import SuperAdminSettingsPage from './pages/superadmin/SettingsPage';

// Company Admin Pages
import CompanyAdminLoginPage from './pages/companyAdmin/LoginPage';
import CompanyAdminDashboardPage from './pages/companyAdmin/DashboardPage';
import CompanyAdminCustomersPage from './pages/companyAdmin/CustomersPage';
import CompanyAdminCustomerDetailPage from './pages/companyAdmin/CustomerDetailPage';
import CompanyAdminUsersPage from './pages/companyAdmin/UsersPage';
import CompanyAdminGroupsPage from './pages/companyAdmin/GroupsPage';
import CompanyAdminSettingsPage from './pages/companyAdmin/SettingsPage';
import AIConfigPage from './pages/companyAdmin/AIConfigPage';
import AIHealthPage from './pages/companyAdmin/AIHealthPage';
import ReportsPage from './pages/companyAdmin/ReportsPage';
import ExpiredFoodsPage from './pages/companyAdmin/ExpiredFoodsPage';
import AIChatsPage from './pages/companyAdmin/AIChatsPage';
import CompanyAdminChatsPage from './pages/companyAdmin/ChatsPage';
import CompanyAdminSubscriptionsPage from './pages/companyAdmin/SubscriptionsPage';
import CompanyAdminPaymentHistoryPage from './pages/companyAdmin/PaymentHistoryPage';
import CompanyAdminAppInstallationsPage from './pages/companyAdmin/AppInstallationsPage';
import CompanyAdminRecipesPage from './pages/companyAdmin/RecipesPage';
import CompanyAdminTicketsPage from './pages/companyAdmin/TicketsPage';
import CompanyAdminPartnersPage from './pages/companyAdmin/PartnersPage';
import CompanyAdminWithdrawalsPage from './pages/companyAdmin/WithdrawalsPage';
import PlaceholderPage from './pages/companyAdmin/PlaceholderPage';

// Partner Portal Pages
import PartnerLoginPage from './pages/partner/LoginPage';
import PartnerDashboardPage from './pages/partner/DashboardPage';
import PartnerCustomersPage from './pages/partner/CustomersPage';
import PartnerPaymentHistoryPage from './pages/partner/PaymentHistoryPage';
import PartnerWithdrawalRequestPage from './pages/partner/WithdrawalRequestPage';
import PartnerWithdrawalHistoryPage from './pages/partner/WithdrawalHistoryPage';
import PartnerSettingsPage from './pages/partner/SettingsPage';

import CustomerLoginPage from './pages/customer/LoginPage';
import CustomerChatPage from './pages/customer/ChatPage';
import CustomerAiChatPage from './pages/customer/AiChatPage';
import CustomerVoiceChatPage from './pages/customer/VoiceChatPage';
import RegisterPage from './pages/RegisterPage';
import RegisterCreateProfilePage from './pages/RegisterCreateProfilePage';
import RegisterSurveyPage from './pages/RegisterSurveyPage';
import DownloadPage from './pages/DownloadPage';
import EntryPage from './pages/EntryPage';
import NotFoundPage from './pages/NotFoundPage';

// Protected Route component for SuperAdmin
const SuperAdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || user?.user_type !== 0) {
    return <Navigate to="/superadmin/login" replace />;
  }

  return children;
};

// Protected Route component for Admin panel
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Admin, Partner, Agent
  const isAdminUser = isAuthenticated && [1, 2, 3].includes(user?.user_type);
  if (!isAdminUser) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route (redirects to dashboard if already logged in)
const SuperAdminPublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated && user?.user_type === 0) {
    return <Navigate to="/superadmin" replace />;
  }

  return children;
};

const AdminPublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isAdminUser = isAuthenticated && [1, 2, 3].includes(user?.user_type);
  if (isAdminUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const CompanyAdminProtectedRoute = ({ children }) => {
  // Separate auth storage for company admin (no dependency on main AuthContext)
  const token = localStorage.getItem('company_admin_token');
  const isAuthed = !!token;

  if (!isAuthed) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Restrict certain pages for Company Admin roles
// - Agents (user_type=3) should not access Users/Groups management pages
const CompanyAdminNonAgentRoute = ({ children }) => {
  const rawUser = localStorage.getItem('company_admin_user');
  if (rawUser) {
    try {
      const u = JSON.parse(rawUser);
      if (Number(u?.user_type) === 3) {
        return <Navigate to="/admin" replace />;
      }
    } catch (e) {
      // ignore
    }
  }
  return children;
};

const CompanyAdminPublicRoute = ({ children }) => {
  const token = localStorage.getItem('company_admin_token');
  if (token) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

// Customer protected route
const CustomerProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('customer_token');
  if (!token) {
    return <Navigate to="/customers/login" replace />;
  }
  return children;
};

// Partner Portal protected route
const PartnerProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('partner_token');
  if (!token) return <Navigate to="/partner/login" replace />;
  return children;
};

const PartnerPublicRoute = ({ children }) => {
  const token = localStorage.getItem('partner_token');
  if (token) return <Navigate to="/partner" replace />;
  return children;
};


function AppRoutes() {
  return (
    <Routes>
      {/* Old Admin Panel Routes */}
      <Route
        path="/login"
        element={
          <AdminPublicRoute>
            <LoginPage />
          </AdminPublicRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <AdminProtectedRoute>
            <DashboardPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/panel/customers"
        element={
          <AdminProtectedRoute>
            <CustomersPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/panel/customers/:id"
        element={
          <AdminProtectedRoute>
            <CustomerDetailPage />
          </AdminProtectedRoute>
        }
      />

      {/* Company Admin Routes */}
      <Route
        path="/admin/login"
        element={
          <CompanyAdminPublicRoute>
            <CompanyAdminLoginPage />
          </CompanyAdminPublicRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminDashboardPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/chats"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminChatsPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/customers"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminCustomersPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/customers/:id"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminCustomerDetailPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/app-installations"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminAppInstallationsPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/recipes"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminRecipesPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminNonAgentRoute>
              <CompanyAdminUsersPage />
            </CompanyAdminNonAgentRoute>
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/subscriptions"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminSubscriptionsPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/payment-history"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminPaymentHistoryPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/groups"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminNonAgentRoute>
              <CompanyAdminGroupsPage />
            </CompanyAdminNonAgentRoute>
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminSettingsPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-config"
        element={
          <CompanyAdminProtectedRoute>
            <AIConfigPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-health"
        element={
          <CompanyAdminProtectedRoute>
            <AIHealthPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <CompanyAdminProtectedRoute>
            <ReportsPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/expired-foods"
        element={
          <CompanyAdminProtectedRoute>
            <ExpiredFoodsPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-chats"
        element={
          <CompanyAdminProtectedRoute>
            <AIChatsPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/tickets"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminTicketsPage />
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminNonAgentRoute>
              <CompanyAdminPartnersPage />
            </CompanyAdminNonAgentRoute>
          </CompanyAdminProtectedRoute>
        }
      />
      <Route
        path="/admin/partners/withdrawals"
        element={
          <CompanyAdminProtectedRoute>
            <CompanyAdminNonAgentRoute>
              <CompanyAdminWithdrawalsPage />
            </CompanyAdminNonAgentRoute>
          </CompanyAdminProtectedRoute>
        }
      />


      {/* Customer Routes */}
      <Route path="/customers" element={<Navigate to="/customers/login" replace />} />
      <Route path="/customers/login" element={<CustomerLoginPage />} />
      <Route
        path="/customers/chat"
        element={
          <CustomerProtectedRoute>
            <CustomerChatPage />
          </CustomerProtectedRoute>
        }
      />
      <Route
        path="/customers/ai-chat"
        element={
          <CustomerProtectedRoute>
            <CustomerAiChatPage />
          </CustomerProtectedRoute>
        }
      />
      <Route
        path="/customers/ai-voice-chat"
        element={
          <CustomerProtectedRoute>
            <CustomerVoiceChatPage />
          </CustomerProtectedRoute>
        }
      />

      {/* Public Registration & Download */}
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register/wizard" element={<RegisterCreateProfilePage />} />
      <Route path="/register/survey" element={<RegisterSurveyPage />} />
      <Route path="/download" element={<DownloadPage />} />

      {/* SuperAdmin Routes */}
      <Route
        path="/superadmin/login"
        element={
          <SuperAdminPublicRoute>
            <SuperAdminLoginPage />
          </SuperAdminPublicRoute>
        }
      />
      <Route
        path="/superadmin"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminDashboardPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/companies"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminCompaniesPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/companies/:company_id/users"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminCompanyUsersPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/settings"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminSettingsPage />
          </SuperAdminProtectedRoute>
        }
      />

      {/* Partner Portal Routes */}
      <Route path="/partner/login" element={<PartnerPublicRoute><PartnerLoginPage /></PartnerPublicRoute>} />
      <Route path="/partner" element={<PartnerProtectedRoute><PartnerDashboardPage /></PartnerProtectedRoute>} />
      <Route path="/partner/customers" element={<PartnerProtectedRoute><PartnerCustomersPage /></PartnerProtectedRoute>} />
      <Route path="/partner/payment-history" element={<PartnerProtectedRoute><PartnerPaymentHistoryPage /></PartnerProtectedRoute>} />
      <Route path="/partner/withdrawals/request" element={<PartnerProtectedRoute><PartnerWithdrawalRequestPage /></PartnerProtectedRoute>} />
      <Route path="/partner/withdrawals/history" element={<PartnerProtectedRoute><PartnerWithdrawalHistoryPage /></PartnerProtectedRoute>} />
      <Route path="/partner/settings" element={<PartnerProtectedRoute><PartnerSettingsPage /></PartnerProtectedRoute>} />

      {/* Root */}
      <Route path="/" element={<EntryPage />} />

      {/* Catch all - no auto redirect to superadmin */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 45_000,
            gcTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
  );

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      storageKey="wingsdiet-theme"
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CompanyAdminAuthProvider>
            <CustomerAuthProvider>
              <PartnerAuthProvider>
                <BrandingProvider>
                  <Router>
                    <AppRoutes />
                    <Toaster
                      position="top-right"
                      richColors
                      toastOptions={{
                        style: {
                          fontFamily: 'Nunito, sans-serif',
                        },
                      }}
                    />
                  </Router>
                </BrandingProvider>
              </PartnerAuthProvider>
            </CustomerAuthProvider>
          </CompanyAdminAuthProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
